import React, { useState } from "react";
import { useTenant } from "../../context/TenantContext";
import { useNotification } from "../../context/NotificationContext";
import { useUniAdminAuth } from "../../context/UniAdminAuthContext";
import { PERMISSIONS } from "../../utils/permissionKeys";
import umService from "../../services/university-admin/umService";
import { useTranslation } from "react-i18next";
import { formatListDate } from "../../utils/formatters";

const UMClubEvents = ({ club, events, onClose, onRefresh }) => {
  const tenantCtx = useTenant();
  const { showSuccess, showError } = useNotification();
  const { can } = useUniAdminAuth();
  const { i18n } = useTranslation();
  const [loading, setLoading] = useState({});

  const handleDeleteEvent = async (event) => {
    const eventName = event.titleEn || event.title || "event";
    if (!window.confirm(`Are you sure you want to delete "${eventName}"?`)) return;

    try {
      setLoading((prev) => ({ ...prev, [`delete_${event.id}`]: true }));
      const response = await umService.deleteClubEvent({ clubId: club.id, eventId: event.id }, tenantCtx);

      if (response.success) {
        showSuccess("Event deleted successfully!");
        onRefresh();
      } else {
        showError("Failed to delete event");
      }
    } catch (error) {
      console.error("Error deleting event:", error);
      showError("Failed to delete event");
    } finally {
      setLoading((prev) => ({ ...prev, [`delete_${event.id}`]: false }));
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      ACTIVE: { bg: "success", icon: "fa-check-circle", text: "Active" },
      COMPLETED: { bg: "info", icon: "fa-flag-checkered", text: "Completed" },
      CANCELLED: { bg: "danger", icon: "fa-times-circle", text: "Cancelled" },
      DRAFT: { bg: "secondary", icon: "fa-edit", text: "Draft" },
      PENDING: { bg: "warning", icon: "fa-clock", text: "Pending" },
    };

    const config = statusConfig[status] || statusConfig.DRAFT;

    return (
      <span className={`badge bg-${config.bg} bg-opacity-10 text-${config.bg} fw-bold`}>
        <i className={`fas ${config.icon} me-1`}></i>
        {config.text}
      </span>
    );
  };

  return (
    <div className="club-events">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 className="mb-0">
          <i className="fas fa-calendar-alt me-2"></i>
          Club Events ({events?.length || 0})
        </h5>
        <button className="btn btn-outline-secondary btn-sm" onClick={onClose}>
          <i className="fas fa-times"></i>
        </button>
      </div>

      {events && events.length > 0 ? (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="bg-light">
              <tr>
                <th className="fw-bold">Event</th>
                <th className="fw-bold">Status</th>
                <th className="fw-bold">Start Date</th>
                <th className="fw-bold">End Date</th>
                <th className="fw-bold text-center">Participants</th>
                <th className="fw-bold text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {events.map((event) => (
                <tr key={event.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <div
                        className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center me-3"
                        style={{ width: "40px", height: "40px" }}
                      >
                        <i className="fas fa-calendar-alt text-info"></i>
                      </div>
                      <div>
                        <div className="fw-bold">{event.titleEn || event.title}</div>
                        <small className="text-muted">ID: {event.id}</small>
                      </div>
                    </div>
                  </td>
                  <td>{getStatusBadge(event.eventStatus || event.status)}</td>
                  <td>
                    <span className="text-dark">{formatListDate(event.startDate, i18n.language)}</span>
                  </td>
                  <td>
                    <span className="text-dark">{formatListDate(event.endDate, i18n.language)}</span>
                  </td>
                  <td className="text-center">
                    <span className="badge bg-primary bg-opacity-10 text-primary fw-bold">
                      {event.totalParticipants || 0}
                    </span>
                  </td>
                  <td className="text-center">
                    {can(PERMISSIONS.event.delete) && (
                      <button
                        className="btn btn-outline-danger btn-sm"
                        onClick={() => handleDeleteEvent(event)}
                        disabled={loading[`delete_${event.id}`]}
                        title="Delete Event"
                      >
                        {loading[`delete_${event.id}`] ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <i className="fas fa-trash"></i>
                        )}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-5">
          <i className="fas fa-calendar-alt text-muted mb-3" style={{ fontSize: "3rem" }}></i>
          <h6 className="text-muted">No Events Found</h6>
          <p className="text-muted">This club doesn't have any events yet.</p>
        </div>
      )}
    </div>
  );
};

export default UMClubEvents;
